// cspell:ignore disputifier riskified
import * as z from "zod";

export const reclaimCompetitorKeySchema = z.enum([
  "chargeflow",
  "chargebacks911",
  "disputifier",
  "riskified_disputes",
  "chargepay",
  "chargeback_auto_prevention",
  "chargeblast",
  "chargeback_specialist",
  "quantchargeback",
]);
export type ReclaimCompetitorKey = z.infer<typeof reclaimCompetitorKeySchema>;

export const reclaimCompetitorSignalStatusSchema = z.enum([
  "matched",
  "none_seen",
  "limited_visibility",
  "no_data",
  "unavailable",
]);
export type ReclaimCompetitorSignalStatus = z.infer<
  typeof reclaimCompetitorSignalStatusSchema
>;

export const reclaimCompetitorSignalAppSchema = z.object({
  key: reclaimCompetitorKeySchema,
  label: z.string(),
  observedTitle: z.string(),
  installedAt: z.string().optional(),
  lastConfirmed: z.string().optional(),
});
export type ReclaimCompetitorSignalApp = z.infer<
  typeof reclaimCompetitorSignalAppSchema
>;

export const reclaimCompetitorSignalSchema = z.object({
  status: reclaimCompetitorSignalStatusSchema,
  apps: z.array(reclaimCompetitorSignalAppSchema),
});
export type ReclaimCompetitorSignal = z.infer<
  typeof reclaimCompetitorSignalSchema
>;

export interface ReclaimCompetitorCatalogEntry {
  key: ReclaimCompetitorKey;
  label: string;
  shopifyAppGids: readonly string[];
  titleAliases: readonly string[];
}

export interface ReclaimCompetitorInstalledAppSnapshot {
  title?: string | null;
  shopifyAppGid?: string | null;
  installedAt?: Date | string | null;
  lastConfirmed?: Date | string | null;
}

export const RECLAIM_COMPETITOR_CATALOG = [
  {
    key: "chargeflow",
    label: "Chargeflow",
    shopifyAppGids: ["gid://shopify/App/4704285"],
    titleAliases: ["Chargeflow"],
  },
  {
    key: "chargebacks911",
    label: "Chargebacks911",
    shopifyAppGids: [],
    titleAliases: ["Chargebacks911", "Chargebacks 911"],
  },
  {
    key: "disputifier",
    label: "Disputifier",
    shopifyAppGids: ["gid://shopify/App/3643375"],
    titleAliases: ["Disputifier", "Disputifier: Smart Chargebacks"],
  },
  {
    key: "riskified_disputes",
    label: "Riskified Disputes",
    shopifyAppGids: ["gid://shopify/App/6528817"],
    titleAliases: ["Riskified Disputes"],
  },
  {
    key: "chargepay",
    label: "ChargePay",
    shopifyAppGids: ["gid://shopify/App/3383073"],
    titleAliases: ["ChargePay"],
  },
  {
    key: "chargeback_auto_prevention",
    label: "Chargeback Auto",
    shopifyAppGids: ["gid://shopify/App/102845775873"],
    titleAliases: ["Chargeback: Auto Prevention"],
  },
  {
    key: "chargeblast",
    label: "Chargeblast",
    shopifyAppGids: ["gid://shopify/App/60610150401"],
    titleAliases: ["Chargeblast"],
  },
  {
    key: "chargeback_specialist",
    label: "Chargeback Specialist",
    shopifyAppGids: ["gid://shopify/App/102358908929"],
    titleAliases: ["Chargeback Specialist"],
  },
  {
    key: "quantchargeback",
    label: "QuantChargeback",
    shopifyAppGids: ["gid://shopify/App/261336301569"],
    titleAliases: ["QuantChargeback", "QuantChargeback Alerts"],
  },
] satisfies readonly ReclaimCompetitorCatalogEntry[];

export const UNAVAILABLE_RECLAIM_COMPETITOR_SIGNAL: ReclaimCompetitorSignal = {
  status: "unavailable",
  apps: [],
};

function normalizeTitleForMatch(title: string): string {
  return title
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function toIsoString(
  value: Date | string | null | undefined,
): string | undefined {
  if (!value) {
    return undefined;
  }
  if (typeof value === "string") {
    return value;
  }
  return value.toISOString();
}

function isOnlineStoreTitle(title: string): boolean {
  return normalizeTitleForMatch(title) === "onlinestore";
}

function matchesCatalogEntry(
  snapshot: ReclaimCompetitorInstalledAppSnapshot,
  entry: ReclaimCompetitorCatalogEntry,
): boolean {
  if (
    snapshot.shopifyAppGid &&
    entry.shopifyAppGids.includes(snapshot.shopifyAppGid)
  ) {
    return true;
  }
  if (!snapshot.title) {
    return false;
  }
  const normalizedTitle = normalizeTitleForMatch(snapshot.title);
  return entry.titleAliases.some(
    (alias) => normalizeTitleForMatch(alias) === normalizedTitle,
  );
}

export function classifyReclaimCompetitorSignal(
  snapshots: readonly ReclaimCompetitorInstalledAppSnapshot[],
  catalog: readonly ReclaimCompetitorCatalogEntry[] = RECLAIM_COMPETITOR_CATALOG,
): ReclaimCompetitorSignal {
  if (snapshots.length === 0) {
    return { status: "no_data", apps: [] };
  }

  const matchedApps: ReclaimCompetitorSignalApp[] = [];
  const matchedKeys = new Set<ReclaimCompetitorKey>();
  let hasResolvedNonOnlineStoreApp = false;
  let hasUnresolvedApp = false;

  for (const snapshot of snapshots) {
    if (!snapshot.title && !snapshot.shopifyAppGid) {
      hasUnresolvedApp = true;
      continue;
    }

    if (snapshot.title && !isOnlineStoreTitle(snapshot.title)) {
      hasResolvedNonOnlineStoreApp = true;
    }

    const match = catalog.find((entry) => matchesCatalogEntry(snapshot, entry));
    if (!match || matchedKeys.has(match.key)) {
      continue;
    }

    matchedKeys.add(match.key);
    const installedAt = toIsoString(snapshot.installedAt);
    const lastConfirmed = toIsoString(snapshot.lastConfirmed);
    matchedApps.push({
      key: match.key,
      label: match.label,
      observedTitle: snapshot.title ?? match.label,
      ...(installedAt ? { installedAt } : {}),
      ...(lastConfirmed ? { lastConfirmed } : {}),
    });
  }

  if (matchedApps.length > 0) {
    return { status: "matched", apps: matchedApps };
  }

  if (hasUnresolvedApp || !hasResolvedNonOnlineStoreApp) {
    return { status: "limited_visibility", apps: [] };
  }

  return { status: "none_seen", apps: [] };
}
